import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { calculateMaxBoxSize } from '@src/utils';
import { useSettingsContext } from '@src/contexts';
import { useAudioStreamStore, usePeersStore, useRoomActionsStore, useVideoStreamStore } from '@src/stores';

import { CONTAINER_MARGIN, MAX_BOXES } from '@src/constants';
import { usePagination } from '@src/hooks';
import { IVideoStream } from '@src/types';

const useVideosContainer = () => {
  const [grid, setGrid] = useState({
    boxWidth: 1,
    boxHeight: 1,
    cols: 1,
  });
  const [hasSpokenAfterUnmute, setHasSpokenAfterUnmute] = useState<Set<string>>(new Set());
  const videosContainerRef = useRef<HTMLDivElement>(null);
  const { selectedLayout, selectedAspectRatio } = useSettingsContext();

  const { raisedHandsQueue } = usePeersStore(
    useShallow((state) => ({
      raisedHandsQueue: state.raisedHandsQueue,
    }))
  );

  const { videoStreams } = useVideoStreamStore(
    useShallow((state) => ({
      videoStreams: state.videoStreams,
    }))
  );

  const { audioStreams } = useAudioStreamStore(
    useShallow((state) => ({
      audioStreams: state.audioStreams,
    }))
  );

  const { isWhiteboardActive } = useRoomActionsStore(
    useShallow((state) => ({
      isWhiteboardActive: state.whiteboardSettings.isActive,
    }))
  );

  // Create map for raised hand order for faster lookup
  const raisedHandOrderMap = useMemo(() => {
    return new Map(raisedHandsQueue.map((peerId, index) => [peerId, index]));
  }, [raisedHandsQueue]);

  const { pinVideo, currentUser } = useMemo(() => {
    const pinnedVideoStream = videoStreams.find(({ type, isPinned }) => !!isPinned && type === 'videoType');
    const shareScreenStream = videoStreams.find(({ type, isPinned }) => !!isPinned && type === 'screenType');
    const pinVideo = pinnedVideoStream || shareScreenStream;
    const currentUser = videoStreams.find((video) => video.isCurrentUser);

    return {
      pinVideo,
      currentUser,
    };
  }, [videoStreams]);

  const filteredVideos = useMemo(() => {
    return videoStreams.filter(
      ({ id, type, isHideMe }) =>
        !isHideMe && !(id === pinVideo?.id && !isWhiteboardActive) && ['screenType', 'videoType'].includes(type)
    );
  }, [videoStreams, pinVideo?.id, isWhiteboardActive]);

  // Single sorted array for videos with priority: raised-hand > talking > video-on
  const sortedVideos = useMemo(() => {
    return filteredVideos.slice().sort((a, b) => {
      const aRaised = raisedHandOrderMap.has(a.peerId);
      const bRaised = raisedHandOrderMap.has(b.peerId);

      // Priority 1: raised-hand: sort by their order in raisedHandsQueue
      if (aRaised && bRaised) {
        return (raisedHandOrderMap.get(a.peerId) ?? Infinity) - (raisedHandOrderMap.get(b.peerId) ?? Infinity);
      }
      if (aRaised) return -1;
      if (bRaised) return 1;

      // Priority 2: Mic  (check audioStreams)
      const aAudio = audioStreams.find((audio) => audio.peerId === a.peerId);
      const bAudio = audioStreams.find((audio) => audio.peerId === b.peerId);
      const aMicOn = aAudio && aAudio.muted === false && hasSpokenAfterUnmute.has(a.peerId);
      const bMicOn = bAudio && bAudio.muted === false && hasSpokenAfterUnmute.has(b.peerId);
      if (aMicOn && !bMicOn) return -1;
      if (!aMicOn && bMicOn) return 1;

      // Priority 3: Camera
      const aCamOn = a.stream !== null;
      const bCamOn = b.stream !== null;
      if (aCamOn && !bCamOn) return -1;
      if (!aCamOn && bCamOn) return 1;

      // If none of the conditions differentiate, leave order unchanged
      return 0;
    });
  }, [filteredVideos, raisedHandOrderMap, audioStreams, hasSpokenAfterUnmute]);

  const paginatedVideoList = useMemo(
    () => sortedVideos.slice(!isWhiteboardActive && !pinVideo && selectedLayout !== 'TILED' ? 1 : 0),
    [sortedVideos, isWhiteboardActive, pinVideo, selectedLayout]
  );

  const calculateGrid = useCallback(
    (count?: number) => {
      if (!videosContainerRef.current) return;

      // Use passed count, or default to 1 if undefined (should be passed by component)
      const numberOfBoxes = Math.min(MAX_BOXES, count || 1);
      const { width: maxWidth, height: maxHeight } = videosContainerRef.current.getBoundingClientRect();

      const { boxHeight, boxWidth, cols } = calculateMaxBoxSize(
        maxWidth,
        maxHeight,
        numberOfBoxes,
        CONTAINER_MARGIN,
        !!pinVideo,
        selectedAspectRatio
      );

      setGrid({
        boxWidth,
        boxHeight,
        cols,
      });
    },
    [pinVideo, selectedAspectRatio]
  );

  // Initial calculation and resize handling.
  // Note: layout effects for specific video count updates should be handled in the component
  // because only the component knows the current page size.
  const { currentPage, hasNext, hasPrev, onNextHandler, onPrevHandler } = usePagination<IVideoStream>({
    perPage: !pinVideo ? MAX_BOXES : 'auto',
    data: paginatedVideoList,
    ref: videosContainerRef,
    childSelector: '.video-screen-container',
  });

  useLayoutEffect(() => {
    calculateGrid(currentPage.length);
  }, [currentPage.length, calculateGrid, isWhiteboardActive]);

  useEffect(() => {
    const containerElement = videosContainerRef.current;
    if (!containerElement) return;

    const resizeObserver = new ResizeObserver(() => {
      resizeObserver.disconnect();
      requestAnimationFrame(() => {
        calculateGrid(currentPage.length);
        if (containerElement.isConnected) {
          resizeObserver.observe(containerElement);
        }
      });
    });
    resizeObserver.observe(containerElement);

    return () => resizeObserver.disconnect();
  }, [calculateGrid, currentPage.length, videosContainerRef, isWhiteboardActive]);

  const copyUrlToClipboard = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl).then(() => {
      toast.success('Link copied to clipboard', {
        autoClose: 1000,
        position: 'top-left',
        className: 'toast toast-black',
      });
    });
  };

  const paginatedVideos = useMemo(() => {
    const otherUsers = pinVideo ? sortedVideos : sortedVideos.filter((video) => !video.isCurrentUser);
    const maxItemPerPage = pinVideo ? 2 : 6;
    if (!otherUsers || otherUsers.length === 0) return [];
    const pages = [];
    for (let i = 0; i < otherUsers.length; i += maxItemPerPage) {
      pages.push(otherUsers.slice(i, i + maxItemPerPage));
    }
    return pages;
  }, [sortedVideos, pinVideo]);

  const {
    shouldShowVerticalBar,
    shouldShowSidebar,
    shouldShowBigScreen,
    shouldShowInstructorLayout,
    shouldShowPagination,
    mainVideo,
    getStartIndex,
  } = useMemo(() => {
    const getStartIndex = () => (!isWhiteboardActive && !pinVideo ? 1 : 0);

    const shouldShowVerticalBar = ['TOP_BAR', 'BOTTOM_BAR'].includes(selectedLayout);

    const shouldShowSidebar =
      selectedLayout === 'SIDEBAR' || (selectedLayout === 'TILED' && (pinVideo || isWhiteboardActive));

    const shouldShowBigScreen = !!pinVideo || isWhiteboardActive || selectedLayout !== 'TILED';

    const shouldShowInstructorLayout = selectedLayout === 'INSTRUCTOR' && sortedVideos[getStartIndex()] != null;

    const shouldShowPagination = selectedLayout === 'TILED' && !isWhiteboardActive && !pinVideo;

    const mainVideo = pinVideo || sortedVideos[0];

    return {
      shouldShowVerticalBar,
      shouldShowSidebar,
      shouldShowBigScreen,
      shouldShowInstructorLayout,
      shouldShowPagination,
      mainVideo,
      getStartIndex,
    };
  }, [selectedLayout, pinVideo, isWhiteboardActive, sortedVideos]);

  // Reset hasSpokenAfterUnmute when someone mutes
  useEffect(() => {
    const mutedPeers = audioStreams.filter((audio) => audio.muted).map((audio) => audio.peerId);

    setHasSpokenAfterUnmute((prev) => {
      const updatedHasSpokenSet = new Set(prev);
      mutedPeers.forEach((peerId) => updatedHasSpokenSet.delete(peerId));
      return updatedHasSpokenSet;
    });
  }, [audioStreams]);

  // Update hasSpokenAfterUnmute when someone speaks
  useEffect(() => {
    const speakingPeers = videoStreams
      .filter((video) => typeof video.audioVolume === 'number' && video.audioVolume > 3)
      .map((video) => video.peerId);

    setHasSpokenAfterUnmute((prev) => {
      const updatedHasSpokenSet = new Set(prev);
      speakingPeers.forEach((peerId) => updatedHasSpokenSet.add(peerId));
      return updatedHasSpokenSet;
    });
  }, [videoStreams]);

  return {
    grid,
    sortedVideos,
    pinVideo,
    videosContainerRef,
    calculateGrid,
    copyUrlToClipboard,
    currentUser,
    isWhiteboardActive,
    paginatedVideos,
    videoStreams,
    selectedLayout,
    shouldShowVerticalBar,
    paginatedVideoList,
    shouldShowSidebar,
    shouldShowBigScreen,
    shouldShowInstructorLayout,
    getStartIndex,
    shouldShowPagination,

    mainVideo,
    currentPage,
    hasNext,
    hasPrev,
    onNextHandler,
    onPrevHandler,
  };
};

export default useVideosContainer;
